<script lang="ts">
	import type { ReviewsBlock as ReviewsBlockType } from '$models/Block/ReviewsBlock';
	import type { Snippet } from 'svelte';
	import { page } from '$app/state';
	import SliderWrapper from '$components/UI/Slider/SliderWrapper.svelte';
	import ReviewsBlockView from './ReviewsBlockView.svelte';

	type Review = ReviewsBlockType['reviews'][number];

	interface Props {
		block: ReviewsBlockType;
	}

	const { block }: Props = $props();

	const mobilePageSize = 3;

	let visibleMobileCount = $state(mobilePageSize);

	const viewMoreLabel = page.data.page.layout.translations.buttonLabels.viewMoreLabel;

	function showMoreReviews() {
		visibleMobileCount = Math.min(visibleMobileCount + mobilePageSize, block.reviews.length);
	}
</script>

{#snippet renderTrack(renderReview: Snippet<[Review, number]>)}
	<div class="reviews-list">
		{#each block.reviews.slice(0, visibleMobileCount) as review, index (`${review.reviewerName}:${review.createdAt}:${index}`)}
			{@render renderReview(review, index)}
		{/each}

		{#if visibleMobileCount < block.reviews.length}
			<div class="reviews-list-footer">
				<button type="button" class="reviews-list-footer-button" onclick={showMoreReviews}>
					<span class="reviews-list-footer-button-icon">+</span>
					<span class="reviews-list-footer-button-label">{viewMoreLabel}</span>
				</button>
			</div>
		{/if}
	</div>

	<div class="reviews-slider">
		<SliderWrapper
			options={{ loop: false, align: 'start' }}
			nav="arrows"
			prevLabel="Previous review"
			nextLabel="Next review"
		>
			{#snippet slides(Slide)}
				{#each block.reviews as review, index (`${review.reviewerName}:${review.createdAt}:${index}`)}
					<Slide>
						{@render renderReview(review, index)}
					</Slide>
				{/each}
			{/snippet}
		</SliderWrapper>
	</div>
{/snippet}

<ReviewsBlockView {block} {renderTrack} showExpandableText />

<style lang="postcss" global>
	.reviews-list {
		display: flex;
		flex-direction: column;
		gap: 16px;

		@media (--lg) {
			display: none;
		}

		&-footer {
			display: flex;
			justify-content: center;
			margin-top: 12px;

			&-button {
				display: flex;
				align-items: center;
				justify-content: center;
				gap: 8px;
				transition: color 0.3s ease;

				&-icon {
					font-size: 20px;
				}

				&-label {
					font-family: var(--title-font-family);
					font-weight: var(--title-font-weight);
					text-transform: var(--title-transform);
				}

				&:hover {
					color: var(--color-promo);
				}
			}
		}
	}

	.reviews-slider {
		display: none;

		&:not(:last-child) {
			margin-bottom: var(--spacing-10);
		}

		.slider {
			--slide-size: 25%;
		}

		@media (--lg) {
			display: block;
		}
	}
</style>
